// DXF file import module — DxfParser is lazy-loaded to reduce initial bundle size
import { calculatePolygonArea } from '../calculations/logic.ts';
import { t } from '../shared/i18n.ts';
import { escapeHtml, showToast } from '../shared/utils.ts';
import { trackDxfImport } from '../shared/analytics.ts';
import { hideModal, showModal } from '../shared/modal.ts';
import { convertArea, getUnitLabels, getUnits } from '../shared/units.ts';

export type DxfPoint = { x: number; y: number };
export type DxfBoundary = { points: DxfPoint[]; layer?: string };
// Minimal subset of the dxf-parser entity shape we actually read. The
// upstream library ships no .d.ts so we model only the fields we touch.
type DxfEntity = { type?: string; layer?: string; vertices?: DxfPoint[] };

let DxfParser: unknown = null;

/**
 * Parse a DXF file and extract boundary polylines.
 * @param {string} fileContent - raw DXF file content
 * @returns {Promise<{ boundaries: Array<{ points: Array<{x: number, y: number}> }>, layers: string[] }>}
 */
export async function parseDxfFile(fileContent: string) {
  if (!DxfParser) {
    const mod = await import('dxf-parser');
    DxfParser = mod.default || mod;
  }
  const parser = new (DxfParser as any)();
  let dxf;
  try {
    dxf = parser.parseSync(fileContent);
  } catch (err) {
    throw new Error(t('dxf.invalidFormat', { message: (err as Error).message }), { cause: err });
  }

  if (!dxf || !dxf.entities) {
    throw new Error(t('dxf.noEntities'));
  }

  const boundaries: DxfBoundary[] = [];
  const layerSet = new Set<string>();

  for (const entity of dxf.entities as DxfEntity[]) {
    if (entity.layer) layerSet.add(entity.layer);

    if (entity.type === 'LWPOLYLINE' || entity.type === 'POLYLINE') {
      const points = extractPolylinePoints(entity);
      if (points.length >= 3) {
        boundaries.push({ points, layer: entity.layer || '0' });
      }
    } else if (entity.type === 'LINE') {
      // Lines are not polylines but we collect them for potential boundary reconstruction
      // Skip for now — we only care about closed polylines for boundaries
    }
  }

  return {
    boundaries,
    layers: Array.from(layerSet).sort(),
  };
}

function extractPolylinePoints(entity: DxfEntity): DxfPoint[] {
  const points: DxfPoint[] = [];
  const vertices = entity.vertices || [];

  for (const v of vertices) {
    points.push({ x: v.x, y: v.y });
  }

  return points;
}

/**
 * Convert DXF coordinate units to meters.
 * @param {Object} options
 * @param {Array<{x: number, y: number}>} options.points
 * @param {string} options.fromUnit - source unit: 'mm', 'cm', 'm', 'in', 'ft'
 * @returns {Array<{x: number, y: number}>}
 */
export function convertDxfUnits({ points, fromUnit = 'mm' }: { points: DxfPoint[]; fromUnit?: string }): DxfPoint[] {
  const factors = {
    mm: 0.001,
    cm: 0.01,
    m: 1,
    in: 0.0254,
    ft: 0.3048,
  };

  const factor = (factors as Record<string, number>)[fromUnit];
  if (!factor) {
    throw new Error(t('dxf.unknownUnit', { unit: fromUnit }));
  }

  return points.map((p) => ({
    x: p.x * factor,
    y: p.y * factor,
  }));
}

/**
 * Select the most likely boundary polyline (the one with the largest area).
 * Uses the shoelace formula to compute area.
 * @param {Array<{ points: Array<{x: number, y: number}> }>} boundaries
 * @returns {{ points: Array<{x: number, y: number}> } | null}
 */
export function selectMainBoundary(boundaries: DxfBoundary[] | null | undefined): DxfBoundary | null {
  if (!boundaries || boundaries.length === 0) return null;
  if (boundaries.length === 1) return boundaries[0];

  let maxArea = -1;
  let best: DxfBoundary | null = null;

  for (const b of boundaries) {
    const area = calculatePolygonArea(b.points);
    if (area > maxArea) {
      maxArea = area;
      best = b;
    }
  }

  return best;
}

// Re-export calculatePolygonArea as computePolygonArea for backwards compatibility
export { calculatePolygonArea as computePolygonArea } from '../calculations/logic.ts';

/**
 * Move a DXF outline into planner coordinates: the drawing's top-left corner
 * lands on the origin, and y is flipped — DXF is y-up, while the planner's
 * metres increase downward on screen. Shifting without the flip imported every
 * outline mirrored top-to-bottom.
 * @param {Array<{x: number, y: number}>} points
 * @returns {Array<{x: number, y: number}>}
 */
export function normalizeBoundaryPoints(points: DxfPoint[] | null | undefined): DxfPoint[] {
  if (!points || points.length === 0) return [];

  const minX = Math.min(...points.map((p) => p.x));
  const maxY = Math.max(...points.map((p) => p.y));

  return points.map((p) => ({
    x: +(p.x - minX).toFixed(3),
    y: +(maxY - p.y).toFixed(3),
  }));
}

/* ── UI wiring ──────────────────────────────────────────────────────
 *
 * The button, the file input and every helper above survived #1017
 * phase 2; the code joining them lived in the v1 entrypoint that was
 * deleted, so DXF rendered in the rail and did nothing at all (#1576) —
 * while the landing page went on advertising the feature. Same deletion
 * took the plot-template picker with it.
 */

type DxfUnit = 'mm' | 'cm' | 'm' | 'in' | 'ft';
const DXF_UNITS: DxfUnit[] = ['mm', 'cm', 'm', 'in', 'ft'];
const PREVIEW_ID = 'dxfPreviewOverlay';

function applyDxfBoundary(boundary: DxfBoundary, unit: DxfUnit): void {
  const converted = convertDxfUnits({ points: boundary.points, fromUnit: unit });
  const points = normalizeBoundaryPoints(converted);
  const planner = (window as unknown as {
    __smartpluvia?: { applyBoundary?: (p: DxfPoint[]) => void };
  }).__smartpluvia;
  planner?.applyBoundary?.(points);
  showToast(t('dxf.importSuccess'), 'success');
  trackDxfImport();
}

/** Preview every closed polyline the file yielded, so the user picks the
 *  outline and the unit the drawing was authored in. Areas are recomputed
 *  on each unit change — that number is how you recognise the right
 *  interpretation when the file does not say. */
function showDxfPreview(boundaries: DxfBoundary[], onApply: (b: DxfBoundary, u: DxfUnit) => void): void {
  document.querySelector('.dxf-preview-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.id = PREVIEW_ID;
  overlay.className = 'dxf-preview-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'dxfPreviewTitle');

  const main = selectMainBoundary(boundaries);
  let selectedIndex = main ? boundaries.indexOf(main) : 0;
  let selectedUnit: DxfUnit = 'mm';

  // Through the shared modal helper, which removes the overlay (onHide) and
  // hands focus back to the DXF button however the preview closes.
  const close = () => hideModal(PREVIEW_ID);

  // Every change re-renders the preview, which throws away the focused
  // element; put focus back on the matching control in the new markup.
  const refocus = (selector: string) => overlay.querySelector<HTMLElement>(selector)?.focus();
  const select = (index: number) => {
    selectedIndex = Math.max(0, Math.min(boundaries.length - 1, index));
    render();
    refocus('.dxf-polyline-item[aria-selected="true"]');
  };

  function render(): void {
    const units = getUnits();
    const options = DXF_UNITS
      .map((u) => `<option value="${u}"${u === selectedUnit ? ' selected' : ''}>${escapeHtml(t(`dxf.unit${u[0].toUpperCase()}${u.slice(1)}`))}</option>`)
      .join('');

    const items = boundaries
      .map((b, i) => {
        const area = calculatePolygonArea(convertDxfUnits({ points: b.points, fromUnit: selectedUnit }));
        const isSelected = i === selectedIndex;
        return `
          <li class="dxf-polyline-item${isSelected ? ' selected' : ''}" data-index="${i}"
              role="option" aria-selected="${isSelected}" tabindex="${isSelected ? 0 : -1}">
            <div class="dxf-polyline-info">
              ${escapeHtml(t('dxf.polylineInfo', { num: i + 1, count: b.points.length }))}
              <br><span class="layer-name">${escapeHtml(t('dxf.layerLabel'))} ${escapeHtml(b.layer || '0')}</span>
            </div>
            <div class="dxf-polyline-area">${convertArea(area, units).toFixed(1)} ${escapeHtml(getUnitLabels(units).area)}</div>
          </li>`;
      })
      .join('');

    overlay.innerHTML = `
      <div class="dxf-preview-modal">
        <div class="dxf-preview-header">
          <h3 id="dxfPreviewTitle">${escapeHtml(t('dxf.importTitle'))}</h3>
          <button type="button" class="dxf-preview-close" aria-label="${escapeHtml(t('dxf.cancel'))}">&times;</button>
        </div>
        <div class="dxf-preview-body">
          <div class="dxf-unit-select">
            <label for="dxfUnit">${escapeHtml(t('dxf.unitLabel'))}</label>
            <select id="dxfUnit">${options}</select>
          </div>
          <ul class="dxf-polyline-list" role="listbox" aria-labelledby="dxfPreviewTitle">${items}</ul>
        </div>
        <div class="dxf-preview-footer">
          <button type="button" class="dxf-btn-cancel">${escapeHtml(t('dxf.cancel'))}</button>
          <button type="button" class="dxf-btn-apply">${escapeHtml(t('dxf.apply'))}</button>
        </div>
      </div>`;

    overlay.querySelector('.dxf-preview-close')?.addEventListener('click', close);
    overlay.querySelector('.dxf-btn-cancel')?.addEventListener('click', close);
    overlay.querySelector('.dxf-btn-apply')?.addEventListener('click', () => {
      onApply(boundaries[selectedIndex], selectedUnit);
      close();
    });
    overlay.querySelector<HTMLSelectElement>('#dxfUnit')?.addEventListener('change', (e) => {
      selectedUnit = (e.target as HTMLSelectElement).value as DxfUnit;
      render();
      refocus('#dxfUnit');
    });
    overlay.querySelectorAll<HTMLElement>('.dxf-polyline-item').forEach((item) => {
      item.addEventListener('click', () => select(Number(item.dataset.index)));
    });
    // The outlines were click-only rows; as a listbox with one tab stop the
    // arrow keys move the choice.
    overlay.querySelector('.dxf-polyline-list')?.addEventListener('keydown', (e) => {
      const key = (e as KeyboardEvent).key;
      const moves: Record<string, number> = {
        ArrowDown: selectedIndex + 1, ArrowUp: selectedIndex - 1, Home: 0, End: boundaries.length - 1,
      };
      if (!(key in moves)) return;
      e.preventDefault();
      select(moves[key]);
    });
  }

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  render();
  document.body.appendChild(overlay);
  showModal(PREVIEW_ID, { onHide: () => overlay.remove() });
  refocus('.dxf-polyline-item[aria-selected="true"]');
}

/** Wire the DXF rail button to the hidden file input and the preview. */
export function initDxfImport(): void {
  const btn = document.getElementById('btnImportDXF');
  const input = document.getElementById('dxfFileInput') as HTMLInputElement | null;
  if (!btn || !input) return;

  btn.addEventListener('click', () => input.click());

  input.addEventListener('change', () => {
    const file = input.files?.[0];
    // Clear immediately so picking the same file twice still fires `change`.
    input.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const result = await parseDxfFile(String(ev.target?.result ?? ''));
        if (!result.boundaries.length) {
          showToast(t('dxf.noPolylines'), 'warning');
          return;
        }
        showDxfPreview(result.boundaries, applyDxfBoundary);
      } catch (err) {
        showToast((err as Error).message || t('dxf.readError'), 'error');
      }
    };
    reader.onerror = () => showToast(t('dxf.readError'), 'error');
    reader.readAsText(file);
  });
}
